import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { BacklogSummary } from '@aica/api-types';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/modals/ConfirmDialog';
import { usePersistedState } from '@/hooks/usePersistedState';
import { formatExactDollars, toSupportedCurrency } from '@/lib/currency';
import { quoteMessageParts } from './quote-message';
import { acceptedPriceOf } from './start-price';
import type { ProcessingState } from './types';

type BacklogRange = 'all' | 'today';

interface BacklogCardProps {
  /** Право запускать накопленное — то же серверное решение, что у баннера. */
  canStart: boolean;
  summary: BacklogSummary | null;
  isProcessing: boolean;
  /** Организация: у каждой своё «скрыто», иначе отказ в одной глушит все. */
  orgId: string | null;
  /** Окно и цена его партии, показанная на подтверждении (#1396). */
  onProcess: ProcessingState['processBacklog'];
  /**
   * Сводка за окно — для варианта «за сегодня». Приходит функцией, а не
   * готовыми данными: SaaS и embedded ходят разными API-клиентами, а запрос
   * нужен только если пользователь действительно выбрал «сегодня».
   *
   * Без неё карточка не соврёт: подтверждение и кнопка тогда не называют
   * сумму, которую не посчитали (см. `rangeSummary` ниже).
   */
  fetchRangeSummary?: (range: { from: string }) => Promise<BacklogSummary>;
}

/** Начало текущих суток в ISO — граница для варианта «за сегодня». */
function startOfToday(): string {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
}

/**
 * Накопленное до включения анализа.
 *
 * Отдельное действие, а не часть тумблера: включение обрабатывает только
 * новые звонки, иначе одно нажатие списывало бы за всё накопленное без
 * спроса. Цена приходит с сервера и здесь не пересчитывается — она обязана
 * совпасть с тем, что реально спишется.
 *
 * Два шага между кликом и списанием появились потому, что их не было ни
 * одного: единственная кнопка тратила деньги с первого нажатия, а убрать
 * карточку с глаз можно было только заплатив — отказаться она не предлагала.
 * Теперь «Скрыть» закрывает её, а подтверждение называет сумму до списания.
 */
export function BacklogCard({
  canStart,
  summary,
  isProcessing,
  orgId,
  onProcess,
  fetchRangeSummary,
}: BacklogCardProps) {
  const { t } = useTranslation('records');
  const [range, setRange] = useState<BacklogRange>('all');
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Граница «сегодня» фиксируется на время жизни карточки: пересчёт на каждом
  // рендере менял бы queryKey и дёргал запрос заново.
  const [todayFrom] = useState(startOfToday);

  const todayQuery = useQuery({
    queryKey: ['records', 'backlog-summary', orgId, todayFrom],
    enabled: range === 'today' && !!fetchRangeSummary,
    staleTime: 60 * 1000,
    queryFn: () => fetchRangeSummary!({ from: todayFrom }),
  });

  // Запоминаем объём на момент отказа, а не сам факт отказа: «нет» относится
  // к тому, что накопилось сейчас. Дорастёт до большего — предложение честно
  // возвращается, и молчаливо копиться дальше уже не будет.
  const [dismissedAtRecords, setDismissedAtRecords] = usePersistedState<number | null>(
    `aica.backlog.dismissedAtRecords.${orgId ?? 'unknown'}`,
    null
  );

  // Нечего обрабатывать — нечего и показывать. Режим анализа (авто/ручной) на
  // видимость не влияет: и в автоматическом режиме накопленное может
  // существовать (проект с выключенным `autoAnalysis`, история до переключения
  // режима), и «Обработать накопленное» обязано работать одинаково в обоих —
  // это тот же ручной запуск, что и у одиночной записи. `totalRecords` — весь
  // накопленный объём; `records` — лишь та партия, что уйдёт за один запуск.
  // Карточка появляется, пока накоплено хоть что-то.
  if (!canStart || !summary) return null;

  // Ждущие записи источника с остановленной обработкой (#2709) не входят ни в
  // `totalRecords`, ни в цену: их не запустить. Но молчать о них нельзя — без
  // строки ниже организация, у которой ждут только такие записи, читалась бы
  // как «ничего не ждёт». Число берётся за то же окно, что и цена.
  const pausedCount =
    range === 'today'
      ? (todayQuery.data?.processingPaused ?? 0)
      : summary.processingPaused;
  const pausedNote =
    pausedCount > 0
      ? t('records.start.resultProcessingPausedNote', { count: pausedCount })
      : null;

  if (summary.totalRecords === 0) {
    if (!pausedNote) return null;
    return (
      <div
        className="mb-4 rounded-card border border-border bg-bg-surface p-4"
        data-testid="backlog-paused-only"
      >
        <p className="text-sm text-fg-muted">{pausedNote}</p>
      </div>
    );
  }
  if (dismissedAtRecords !== null && summary.totalRecords <= dismissedAtRecords)
    return null;

  // Что произойдёт по нажатию, а не что накопилось всего: подтверждение с
  // числами всего бэклога при выбранном «сегодня» обещало 300 разговоров за
  // прогон, который обработает 10.
  const rangeSummary = range === 'today' ? (todayQuery.data ?? null) : summary;
  // Оценку за окно не посчитали и уже не посчитаем: запрос упал. Это не то же
  // самое, что «ещё считаем» — кнопка оставалась кликабельной с подписью
  // «Считаем…», а подтверждение продолжало обещать, что сумма считается.
  const rangeFailed = range === 'today' && todayQuery.isError;
  // Одна готовая строка суммы, а не число + код валюты в двух дырках копии.
  // Копия несла свой знак `$` перед `{{price}}` И подставляла `{{currency}}`,
  // так что сумма была подписана дважды («$12.34 USD») во всех четырёх
  // локалях, а глиф был зашит в текст, а не выведен из валюты записи. Символ
  // теперь даёт `formatDollars` по валюте, которую вернул сервер.
  // Локаль намеренно не передаётся: у `formatDollars` по умолчанию `en-US`, и
  // им же нарисованы все суммы на дашбордах. Взять сюда язык интерфейса
  // значило бы показать «10,14 $» в подтверждении списания и «$10.14» в
  // биллинге на соседнем экране — про одни и те же деньги.
  //
  // Сумма показывается ТОЧНОЙ, включая доли цента. Ставки поддерживают четыре
  // знака, деньги хранятся с шестью, а округление до двух превращало подпись в
  // обещание: $0.014 подписывались «$0.01», а $0.004 — «$0.00», то есть
  // «бесплатно», после чего воркфлоу списывал. Целые центы при этом выглядят
  // как обычно — лишние знаки появляются, только когда они в сумме есть.
  const amount =
    rangeSummary !== null
      ? formatExactDollars(
          rangeSummary.priceUsd,
          toSupportedCurrency(rangeSummary.currency)
        )
      : null;
  // Звонки, чья длительность неизвестна, входят в сумму нулём — а спишутся по
  // настоящей длительности, измеренной на транскрайбе, когда файл приедет.
  // Пока их есть хоть один, названная сумма — нижняя граница, а не цена.
  const unpricedCalls = rangeSummary?.unknownDurationCalls ?? 0;
  // Пока оценка за окно не пришла, считать её бесплатной нельзя — это
  // обещание «спишется 0». И «бесплатно» не говорится, если в партии есть
  // звонок без известной длительности: ноль в сумме — это отсутствие данных,
  // а не отсутствие цены.
  const isFree = rangeSummary ? rangeSummary.priceUsd <= 0 && unpricedCalls === 0 : false;
  const rangeCounted = rangeSummary !== null;
  const postpaidSuffix = rangeSummary?.billingMode === 'postpaid' ? 'Postpaid' : '';
  // За какой набор названа сумма — и совпадает ли он с набором в заголовке.
  //
  // Заголовок говорит про ВЕСЬ накопленный объём, цена — только про то, что
  // уйдёт одним запуском: не больше `batchLimit` записей, а при выбранном
  // окне — лишь за него. Пока строка цены молчала про свой набор, две
  // соседние строки читались как одно утверждение. На проде это выглядело
  // так: «Накопичено розмов: 760 · 1291.4 хв» и сразу под ней «Понад тариф:
  // $16.8545» — тогда как $16.85 стоили 349.6 из тех минут, а весь объём
  // стоил бы $78.07 в четыре запуска. Владелец организации прочитал это как
  // ошибку в расчёте, и был прав ровно наполовину: ошибалась подпись (#546).
  //
  // Набор называется ТОЛЬКО когда он МЕНЬШЕ заголовка. У организации, чьё
  // накопленное уходит за один запуск, обе строки и так про одно и то же, и
  // повторять там числа заголовка значит лишь мешать читать.
  //
  // Числа идут без существительного («за 200 из 760»), и это не экономия
  // слов: существительное после числительного требует четырёх форм в ru/uk,
  // а называть его второй раз подряд под заголовком, где оно уже названо,
  // незачем.
  const pricedRecords = rangeSummary?.records ?? 0;
  const pricedMinutes = rangeSummary?.minutes ?? 0;
  const priceCoversSubset = rangeCounted && pricedRecords < summary.totalRecords;
  const subsetParams = {
    records: pricedRecords,
    total: summary.totalRecords,
    minutes: pricedMinutes,
  };

  // Текст цены — общий с запуском выбранного (quote-message.ts, #1389): одна
  // формулировка на оба экрана, где клиент соглашается платить. Здесь
  // остаются только ветки, которых нет у запуска выбранного: сумма за окно
  // ещё считается или посчитать её не удалось.
  //
  // Строка про чаты объясняет, откуда у переписки минуты (#414 перевёл чат на
  // расход гранта — двести переписок показываются как «200.0 мин»); строка
  // про звонки без длительности — почему сумма нижняя граница. Обе видны и на
  // самой карточке, не только в диалоге.
  const priceParts = rangeSummary
    ? quoteMessageParts(t, 'records.backlog', rangeSummary)
    : null;
  const confirmMessage = rangeFailed
    ? t('records.backlog.confirmMessageRangeFailed', {
        defaultValue:
          'The amount for today could not be calculated. Try again — nothing is charged until it is known.',
      })
    : priceParts
      ? priceParts.base
      : // Сумму не посчитали — говорим только то, что знаем точно: что именно
        // попадёт в обработку.
        t('records.backlog.confirmMessageRangePending');
  const chatNote = priceParts?.chatNote ?? null;
  const unpricedNote = priceParts?.unpricedNote ?? null;

  // Запуск берёт не больше batchLimit записей за раз. Молчать об остатке —
  // значит показать «отправлено 200», когда человек ждал, что уйдут все 500.
  const remaining = rangeCounted
    ? Math.max(0, rangeSummary.totalRecords - rangeSummary.records)
    : 0;
  const confirmMessageWithRemainder = [
    confirmMessage,
    chatNote,
    unpricedNote,
    remaining > 0
      ? t('records.backlog.confirmRemainder', { remaining, count: remaining })
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="mb-4 rounded-card border border-border bg-bg-surface p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-medium text-fg">
            {t('records.backlog.title', {
              records: summary.totalRecords,
              minutes: summary.totalMinutes,
            })}
          </p>
          {/* Only once the estimate for the SELECTED window has arrived.
              `amount` is null while the "today" query is in flight, and
              rendering the price line anyway printed a sum with an empty
              amount right next to a button that correctly said the estimate
              was still pending. The currency it is formatted in comes from the
              same summary the number does, never from the whole-backlog one. */}
          {/* Постоплата платит по договору, мимо минут тарифа: «сверх тарифа»
              и «покрывается минутами тарифа» были бы у неё неправдой. */}
          {rangeCounted && (
            <p className="mt-1 text-sm text-fg-muted">
              {isFree
                ? priceCoversSubset
                  ? t(`records.backlog.freeOfChargeSubset${postpaidSuffix}`, subsetParams)
                  : t(`records.backlog.freeOfCharge${postpaidSuffix}`)
                : unpricedCalls > 0
                  ? priceCoversSubset
                    ? t(`records.backlog.priceFromSubset${postpaidSuffix}`, {
                        amount,
                        ...subsetParams,
                      })
                    : t(`records.backlog.priceFrom${postpaidSuffix}`, { amount })
                  : priceCoversSubset
                    ? t(`records.backlog.priceSubset${postpaidSuffix}`, {
                        amount,
                        ...subsetParams,
                      })
                    : t(`records.backlog.price${postpaidSuffix}`, { amount })}
            </p>
          )}
          {/* Сумма без объяснения читается как ошибка: у партии из чатов
              минут нет, а деньги есть. */}
          {chatNote ? <p className="mt-1 text-xs text-fg-subtle">{chatNote}</p> : null}
          {/* И то же для «от»: причина названа прямо, а не оставлена
              догадке. */}
          {unpricedNote ? (
            <p className="mt-1 text-xs text-fg-subtle">{unpricedNote}</p>
          ) : null}
          {/* Эти записи в партию и в цену не входят — но и не исчезли. */}
          {pausedNote ? (
            <p className="mt-1 text-xs text-fg-subtle" data-testid="backlog-paused-note">
              {pausedNote}
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <select
            aria-label={t('records.backlog.rangeLabel')}
            value={range}
            onChange={(event) => setRange(event.target.value as BacklogRange)}
            className="rounded-card border border-border bg-bg px-3 py-2 text-sm text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="all">{t('records.backlog.rangeAll')}</option>
            <option value="today">{t('records.backlog.rangeToday')}</option>
          </select>

          <Button
            onClick={() => setConfirmOpen(true)}
            // Ждём оценку за выбранное окно: списание без названной суммы —
            // ровно то, от чего избавило подтверждение. Именно «оценки нет», а
            // не `isLoading`: запрос на паузе без сети в TanStack v5 не
            // `isLoading`, и кнопка открывала подтверждение, которое ничего не
            // отправляло (code review r2 на #1454).
            disabled={isProcessing || !rangeCounted || rangeFailed}
            data-testid="backlog-process-button"
          >
            {rangeFailed
              ? t('records.backlog.actionRangeFailed', {
                  defaultValue: 'Estimate unavailable',
                })
              : !rangeCounted
                ? t('records.backlog.actionRangePending')
                : isFree
                  ? t('records.backlog.actionFree')
                  : unpricedCalls > 0
                    ? t('records.backlog.actionFrom', { amount })
                    : t('records.backlog.action', { amount })}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissedAtRecords(summary.totalRecords)}
            aria-label={t('records.backlog.dismiss')}
            title={t('records.backlog.dismiss')}
            data-testid="backlog-dismiss-button"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        // Подтверждение несёт показанную партию: срез `asOf` уходит в `to`, и
        // сервер запускает ту же партию, а не ту, в которую дописались записи,
        // принятые после показа; цена — та, что на экране, и сервер не
        // запустит, если по этой партии она выросла (#1396). Кнопка неактивна,
        // пока оценки окна нет, поэтому сводка здесь всегда есть.
        onConfirm={() => {
          if (!rangeSummary) return;
          onProcess(
            {
              ...(range === 'today' ? { from: todayFrom } : {}),
              ...(rangeSummary.asOf ? { to: rangeSummary.asOf } : {}),
            },
            acceptedPriceOf(rangeSummary)
          );
        }}
        title={t('records.backlog.confirmTitle')}
        message={confirmMessageWithRemainder}
        confirmText={t('records.backlog.confirmAction')}
        variant={isFree ? 'info' : 'warning'}
      />
    </div>
  );
}
